import { React, useState } from 'react';
import { FaSearch } from 'react-icons/fa';
import { BiSliderAlt } from 'react-icons/bi';
import { IoClose } from 'react-icons/io5';
import toast from 'react-hot-toast';

import { searchEmails } from '../../services/api';
import AdvancedEmailSearchForm from '../../components/form/AdvancedEmailSearchForm';
import { DataState } from '../../context/DataProvider';
import { useEmails } from '../../context/EmailsProvider';

export default function EmailSearch({
  setIsSearchBoxOpen,
  isAdvanceSearchOpen,
  setIsAdvancedSearchOpen,
  isOutlook,
}) {
  const { userGoogleId } = DataState();
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const { setEmails, setIsSearchDone, setTotalEmails } = useEmails();
  const [formData, setFormData] = useState({
    searchIn: 'all folders',
    from: '',
    to: '',
    cc: '',
    subject: '',
    keyword: '',
    dateFrom: '',
    dateTo: '',
    readStatus: 'all',
    attachments: false,
  });

  const handleReset = () => {
    setFormData({
      searchIn: 'all folders',
      from: '',
      to: '',
      cc: '',
      subject: '',
      keyword: '',
      dateFrom: '',
      dateTo: '',
      readStatus: 'all',
      attachments: false,
    });
  };

  const handleSearchEmails = async () => {
    if (
      !searchQuery.trim() &&
      !formData.subject.trim() &&
      !formData.keyword.trim() &&
      !formData.from.trim() &&
      !formData.to.trim() &&
      !formData.cc.trim() &&
      !formData.dateFrom.trim() &&
      !formData.dateTo.trim() &&
      !formData.attachments &&
      formData.readStatus === 'all'
    ) {
      toast.error('Please enter something to search!');
      return;
    }

    setLoading(true);
    try {
      let filterConditions = [];
      let finalSearchQuery = searchQuery || ''; // Initialize with existing search query

      if (isOutlook) {
        // Outlook Search - Uses filterConditions

        // Handle Subject Search
        if (formData.subject) {
          filterConditions.push(`contains(subject, '${formData.subject}')`);
        }

        // Handle "From" Filter
        if (formData.from) {
          filterConditions.push(
            `from/emailAddress/address eq '${formData.from}'`,
          );
        }

        // Handle "To" Filter
        if (formData.to) {
          filterConditions.push('toRecipients/any()');
        }

        // Handle "CC" Filter
        if (formData.cc) {
          filterConditions.push(
            `ccRecipients/any(r: r/emailAddress/address eq '${formData.cc}')`,
          );
        }

        // Handle Read/Unread Status
        if (formData.readStatus !== 'all') {
          filterConditions.push(`isRead eq ${formData.readStatus === 'read'}`);
        }

        // Handle Attachments
        if (formData.attachments) {
          filterConditions.push(`hasAttachments eq true`);
        }

        // Handle Date Range
        if (formData.dateFrom && formData.dateTo) {
          filterConditions.push(
            `receivedDateTime ge ${formData.dateFrom}T00:00:00Z AND receivedDateTime le ${formData.dateTo}T23:59:59Z`,
          );
        }
      } else {
        // Gmail Search - Merge all formData fields into the search query

        let gmailSearchParts = [];

        if (formData.subject) {
          gmailSearchParts.push(`subject:(${formData.subject})`);
        }
        if (formData.from) {
          gmailSearchParts.push(`from:(${formData.from})`);
        }
        if (formData.to) {
          gmailSearchParts.push(`to:(${formData.to})`);
        }
        if (formData.cc) {
          gmailSearchParts.push(`cc:(${formData.cc})`);
        }
        if (formData.keyword) {
          gmailSearchParts.push(`"${formData.keyword}"`);
        }
        if (formData.dateFrom && formData.dateTo) {
          gmailSearchParts.push(
            `after:${formData.dateFrom} before:${formData.dateTo}`,
          );
        }
        if (formData.readStatus === 'read') {
          gmailSearchParts.push('is:read');
        } else if (formData.readStatus === 'unread') {
          gmailSearchParts.push('is:unread');
        }
        if (formData.attachments) {
          gmailSearchParts.push('has:attachment');
        }

        // Combine Gmail-specific search parts
        if (gmailSearchParts.length) {
          finalSearchQuery += gmailSearchParts.join(' ');
        }
      }

      // Encode search query and filter conditions
      // const encodedSearch = finalSearchQuery ? encodeURIComponent(finalSearchQuery.trim()) : "";
      const encodedFilter =
        isOutlook && filterConditions.length
          ? filterConditions.join(' AND ')
          : '';

      const response = await searchEmails(
        userGoogleId,
        finalSearchQuery,
        encodedFilter,
        1,
        20,
        isOutlook,
      );
      setTotalEmails(response?.total_count || 0);
      setEmails(response?.emails);
      toast.success('Emails retrieved successfully!');
      setIsAdvancedSearchOpen(false);
    } catch (error) {
      toast.error('Failed to fetch emails. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleclose = () => {
    handleReset();
    if (searchQuery.trim()) {
      setSearchQuery('');
      return;
    }
    if (isAdvanceSearchOpen) {
      setIsAdvancedSearchOpen(false);
    } else {
      setIsSearchBoxOpen(false);
    }
    setIsSearchDone((prev) => prev + 1);
  };

  return (
    <div className="flex w-full items-center">
      {/* Blurry Background Overlay */}
      {/* <div className="absolute inset-0 bg-black bg-opacity-30 backdrop-blur-sm "></div> */}

      {/* Search Box */}
      <div className="relative inline-block w-full gap-2  pb-1 transition-all duration-300">
        <div className="flex justify-between gap-2">
          <div className="flex h-10 w-full items-center justify-between rounded-r-lg border border-zinc-700 bg-zinc-900  px-3 py-2 text-zinc-300 shadow-md">
            <div className="flex w-full items-center">
              <FaSearch className="mr-2 text-zinc-400" size={18} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search emails..."
                className="w-full bg-transparent text-sm placeholder-zinc-500 focus:outline-none"
              />
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsAdvancedSearchOpen(!isAdvanceSearchOpen)}
                className="flex cursor-pointer items-center justify-center rounded-full p-1 hover:bg-zinc-700"
              >
                <BiSliderAlt
                  className="text-zinc-400 hover:text-zinc-300"
                  size={20}
                />
              </button>
            </div>
          </div>
          {!isAdvanceSearchOpen && (
            <div className="flex  items-center rounded-lg border border-zinc-700 bg-zinc-900 px-2 text-zinc-300 shadow-md hover:bg-zinc-700">
              <button
                onClick={handleSearchEmails}
                disabled={loading}
                className={` flex cursor-pointer items-center justify-center rounded-lg  p-1 text-zinc-400 hover:bg-zinc-700 hover:text-zinc-300
                            ${loading ? 'animate-pulse' : ''}
                            `}
              >
                {loading ? 'Searching...' : 'Search'}
              </button>
            </div>
          )}
          {/* eslint-disable-next-line */}
          <div
            onClick={handleclose}
            className="flex  items-center rounded-l-lg border border-zinc-700 bg-zinc-900 px-2 pl-1 text-zinc-300 shadow-md hover:bg-zinc-700"
          >
            <button>
              <IoClose
                className="text-zinc-400 hover:text-zinc-300"
                size={20}
              />
            </button>
          </div>
        </div>
        {isAdvanceSearchOpen && (
          <div className="absolute right-0 z-[60]  transform  pt-1  shadow-lg ">
            <AdvancedEmailSearchForm
              formData={formData}
              setFormData={setFormData}
              handleSearchEmails={handleSearchEmails}
              loading={loading}
              handleReset={handleReset}
            />
          </div>
        )}
      </div>
    </div>
  );
}
